import type { PropsWithChildren } from 'react'

import { customMedia } from '@space-core/styles'
import clsx from 'clsx'
import { Link as ReactRouterLink, type To } from 'react-router-dom'
import styled, { css } from 'styled-components'

import CardBody from './body'
import CardFooter from './footer'
import CardHoverBackground from './hoverBackground'
import CardHoverInformation from './hoverInformation'
import CardTags from './tags'
import CardThumbnail from './thumbnail'

type LinkProps = {
  as: 'a'
  href: string
}

type LinkRouterProps = {
  as: 'link'
  to: To
}

type DivProps = {
  as: 'div'
}

export type CardProps = PropsWithChildren<{
  className?: string
  onClick?: (event) => void
  'data-test'?: string
}> &
  (LinkProps | LinkRouterProps | DivProps)

const BaseStyle = css`
  outline: none;

  &:is(:focus, :hover) .card-body {
    ${customMedia.greaterThan('md')`
        z-index: 4;
        opacity: 1;
        top: -1.7rem;
        right: -1.7rem;
        bottom: -1.7rem;
        left: -1.7rem;
        transform: translateY(0);
        box-shadow: 0rem 0rem 0rem 0.25rem var(--gray-900);
        transition: all var(--timing) var(--easing) calc(var(--timing) / 3);
    `}
  }

  &:is(:focus, :hover) .card-hover-information {
    ${customMedia.greaterThan('md')`
      transform: translateY(0);
      transition: transform 0.8s var(--easing) calc(var(--timing) / 2);
    `}
  }

  &:is(:focus, :hover) .card-footer {
    ${customMedia.greaterThan('md')`
      opacity: 0;
      visibility: hidden;
    `}
  }
`

const RouterLink = styled(ReactRouterLink)`
  ${BaseStyle}
`

const Link = styled.a`
  ${BaseStyle}
`

const Div = styled.div`
  ${BaseStyle}
`

const Card = (props: CardProps) => {
  const baseClassName = clsx('_position-relative _d-block _rounded-lg', props.className)

  if (props.as === 'a') {
    return (
      <Link
        href={props.href}
        onClick={props.onClick}
        className={clsx('_text-decoration-none', baseClassName)}>
        {props.children}
      </Link>
    )
  }

  if (props.as === 'link') {
    return (
      <RouterLink
        to={props.to}
        onClick={props.onClick}
        className={clsx('_text-decoration-none', baseClassName)}>
        {props.children}
      </RouterLink>
    )
  }

  return (
    <Div
      tabIndex={0}
      role='button'
      onClick={props.onClick}
      data-test={props['data-test']}
      className={clsx('_cursor-pointer', baseClassName)}>
      {props.children}
    </Div>
  )
}

Card.Thumbnail = CardThumbnail
Card.Tags = CardTags
Card.HoverInformation = CardHoverInformation
Card.Footer = CardFooter
Card.Body = CardBody
Card.HoverBackground = CardHoverBackground

export default Card
